import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient, formatApiError } from "../api/client";
import { toast } from "sonner";

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

function generatePassword(length = 20) {
  const alphabet =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!#-_";
  const arr = new Uint32Array(length);
  window.crypto.getRandomValues(arr);
  return Array.from(arr, (n) => alphabet[n % alphabet.length]).join("");
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch {
    return "—";
  }
}

function StatusBadge({ user }) {
  const isAdmin = user.role === "admin";
  const isPro = user.is_pro && user.billing_status === "manual";
  const isStripePro = user.is_pro && user.billing_status !== "manual";
  const isTrial =
    !user.is_pro &&
    user.trial_until &&
    new Date(user.trial_until) > new Date();

  let label = "Expired";
  let cls = "border-zinc-700 bg-zinc-800/40 text-zinc-300";

  if (isAdmin) {
    label = "Admin";
    cls = "border-violet-500/40 bg-violet-500/10 text-violet-200";
  } else if (isPro) {
    label = "Pro · manual";
    cls = "border-emerald-500/40 bg-emerald-500/10 text-emerald-200";
  } else if (isStripePro) {
    label = "Pro · stripe";
    cls = "border-emerald-500/40 bg-emerald-500/10 text-emerald-200";
  } else if (isTrial) {
    label = "Trial";
    cls = "border-amber-500/40 bg-amber-500/10 text-amber-200";
  }
  return (
    <span
      data-testid={`user-status-${user.ulid}`}
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${cls}`}
    >
      {label}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*  Modal primitive                                                           */
/* -------------------------------------------------------------------------- */

function Modal({ open, onClose, title, children, testId }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid={testId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-zinc-800 bg-zinc-950 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-zinc-100">{title}</h2>
          <button
            type="button"
            data-testid="modal-close"
            onClick={onClose}
            className="rounded-md p-1 text-zinc-500 transition-colors hover:bg-zinc-900 hover:text-zinc-100"
            aria-label="Schließen"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Create User modal                                                         */
/* -------------------------------------------------------------------------- */

function CreateUserModal({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState(() => generatePassword(20));
  const [role, setRole] = useState("user");
  const [grantPro, setGrantPro] = useState(true);
  const [years, setYears] = useState(10);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await apiClient.post("/admin/users", {
        email: email.trim().toLowerCase(),
        password,
        role,
        grant_pro: grantPro,
        pro_duration_years: Number(years),
      });
      toast.success(`User ${r.data.email} angelegt`);
      onCreated({ user: r.data, password });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Neuen User anlegen" testId="create-user-modal">
      <form className="space-y-4" onSubmit={submit}>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-zinc-400">
            Email
          </label>
          <input
            type="email"
            required
            data-testid="create-user-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/60"
            placeholder="z.B. review@dealalert.ai"
          />
        </div>
        <div>
          <label className="mb-1 flex items-center justify-between text-xs uppercase tracking-wider text-zinc-400">
            Password (≥ 12 Zeichen)
            <button
              type="button"
              data-testid="create-user-regen-pw"
              onClick={() => setPassword(generatePassword(20))}
              className="text-[10px] text-emerald-400 hover:text-emerald-200"
            >
              ↻ neu generieren
            </button>
          </label>
          <input
            type="text"
            required
            minLength={12}
            data-testid="create-user-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 font-mono text-sm text-zinc-100 outline-none focus:border-emerald-500/60"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-zinc-400">
            Role
          </label>
          <div className="flex gap-2">
            {["user", "admin"].map((r) => (
              <button
                key={r}
                type="button"
                data-testid={`create-user-role-${r}`}
                onClick={() => setRole(r)}
                className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${
                  role === r
                    ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-200"
                    : "border-zinc-800 text-zinc-400 hover:text-zinc-200"
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-2 rounded-md border border-zinc-800 bg-zinc-900/40 p-3">
          <label className="flex items-center gap-2 text-sm text-zinc-200">
            <input
              type="checkbox"
              data-testid="create-user-grant-pro"
              checked={grantPro}
              onChange={(e) => setGrantPro(e.target.checked)}
              className="accent-emerald-500"
            />
            Pro-Zugang aktivieren (manual, kein Stripe)
          </label>
          {grantPro && (
            <div className="flex items-center gap-2 pl-6 text-xs text-zinc-400">
              Dauer:
              <input
                type="number"
                min="0"
                max="50"
                step="0.5"
                data-testid="create-user-years"
                value={years}
                onChange={(e) => setYears(e.target.value)}
                className="w-20 rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-right text-sm text-zinc-100"
              />
              Jahre
            </div>
          )}
        </div>
        {error && (
          <div
            data-testid="create-user-error"
            className="rounded-md border border-rose-900 bg-rose-950/40 px-3 py-2 text-sm text-rose-300"
          >
            {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-zinc-700 px-4 py-2 text-sm text-zinc-200 transition-colors hover:bg-zinc-800 disabled:opacity-40"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={busy || !email || password.length < 12}
            data-testid="create-user-submit"
            className="rounded-md border border-emerald-500/40 bg-emerald-500/15 px-4 py-2 text-sm font-medium text-emerald-100 transition-colors hover:bg-emerald-500/25 disabled:opacity-40"
          >
            {busy ? "Wird angelegt…" : "User anlegen"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/*  Credentials reveal (shown ONCE after create / pw-reset)                   */
/* -------------------------------------------------------------------------- */

function CredentialsReveal({ open, onClose, email, password }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    await navigator.clipboard.writeText(password);
    setCopied(true);
    toast.success("Passwort kopiert");
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Zugangsdaten anzeigen"
      testId="credentials-reveal-modal"
    >
      <div className="space-y-4">
        <p className="text-xs text-amber-300">
          ⚠️ Notiere das Passwort jetzt. Es wird aus Sicherheitsgründen kein zweites Mal angezeigt.
        </p>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-zinc-400">
            Email
          </label>
          <div
            data-testid="reveal-email"
            className="rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 font-mono text-sm text-zinc-100"
          >
            {email}
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-zinc-400">
            Password
          </label>
          <div className="flex items-center gap-2">
            <div
              data-testid="reveal-password"
              className="flex-1 break-all rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 font-mono text-sm text-emerald-100"
            >
              {password}
            </div>
            <button
              type="button"
              data-testid="reveal-copy"
              onClick={onCopy}
              className="rounded-md border border-zinc-700 px-3 py-2 text-xs text-zinc-200 transition-colors hover:bg-zinc-800"
            >
              {copied ? "✓" : "Kopieren"}
            </button>
          </div>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            data-testid="reveal-close"
            className="rounded-md border border-zinc-700 px-4 py-2 text-sm text-zinc-200 transition-colors hover:bg-zinc-800"
          >
            Schließen
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/*  Grant / Revoke / Reset confirmation modals                                */
/* -------------------------------------------------------------------------- */

function ConfirmModal({ open, onClose, onConfirm, title, body, confirmLabel, variant = "default", testId }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };
  const btnCls =
    variant === "danger"
      ? "border-rose-500/40 bg-rose-500/15 text-rose-100 hover:bg-rose-500/25"
      : "border-emerald-500/40 bg-emerald-500/15 text-emerald-100 hover:bg-emerald-500/25";
  return (
    <Modal open={open} onClose={onClose} title={title} testId={testId}>
      <div className="space-y-4">
        <div className="text-sm text-zinc-300">{body}</div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-zinc-700 px-4 py-2 text-sm text-zinc-200 transition-colors hover:bg-zinc-800 disabled:opacity-40"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={run}
            disabled={busy}
            data-testid="confirm-submit"
            className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors disabled:opacity-40 ${btnCls}`}
          >
            {busy ? "…" : confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main page                                                                 */
/* -------------------------------------------------------------------------- */

const FILTERS = [
  { key: "all", label: "Alle", role: null, pro: null },
  { key: "pro", label: "Pro", role: null, pro: true },
  { key: "free", label: "Expired", role: null, pro: false },
  { key: "admin", label: "Admin", role: "admin", pro: null },
];

export default function AdminUsersPage() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [offset, setOffset] = useState(0);
  const limit = 50;

  // Modals
  const [createOpen, setCreateOpen] = useState(false);
  const [revealData, setRevealData] = useState(null);
  const [grantTarget, setGrantTarget] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);

  // Debounce search input
  const searchTimer = useRef(null);
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setOffset(0);
    }, 300);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [search]);

  const activeFilter = useMemo(() => FILTERS.find((f) => f.key === filter) || FILTERS[0], [filter]);

  // react-query handles loading/error/refetch — no setState-in-effect pattern.
  const queryKey = ["admin-users", { search: debouncedSearch, filter: activeFilter.key, offset }];
  const {
    data,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const params = { limit, offset };
      if (debouncedSearch) params.email = debouncedSearch;
      if (activeFilter.role) params.role = activeFilter.role;
      if (activeFilter.pro !== null) params.pro = activeFilter.pro;
      const r = await apiClient.get("/admin/users", { params });
      return r.data;
    },
    keepPreviousData: true,
  });
  const users = data?.items || [];
  const total = data?.total || 0;
  const error = queryError ? formatApiError(queryError) : null;
  const load = useCallback(() => refetch(), [refetch]);

  /* ---------- Mutation handlers --------- */
  const onCreatedFromModal = ({ user, password }) => {
    setCreateOpen(false);
    setRevealData({ email: user.email, password });
    load();
  };

  const onGrantPro = async () => {
    try {
      await apiClient.patch(`/admin/users/${grantTarget.ulid}/grant-pro`, {
        duration_years: 10,
      });
      toast.success(`Pro 10 Jahre für ${grantTarget.email} aktiviert`);
      setGrantTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onRevokePro = async () => {
    try {
      await apiClient.patch(`/admin/users/${revokeTarget.ulid}/revoke-pro`);
      toast.success(`Pro entzogen für ${revokeTarget.email}`);
      setRevokeTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onResetPw = async () => {
    const newPw = generatePassword(20);
    try {
      await apiClient.post(`/admin/users/${resetTarget.ulid}/reset-password`, {
        new_password: newPw,
      });
      const email = resetTarget.email;
      setResetTarget(null);
      setRevealData({ email, password: newPw });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  /* ---------- Render ---------- */
  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
            /api/admin/users
          </span>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">User Management</h1>
          <p className="text-xs text-zinc-500">
            Manuelles Anlegen & Pro-Verwaltung. Stripe-Subs bleiben unberührt.
          </p>
        </div>
        <button
          type="button"
          data-testid="open-create-user"
          onClick={() => setCreateOpen(true)}
          className="rounded-md border border-emerald-500/40 bg-emerald-500/15 px-4 py-2 text-sm font-medium text-emerald-100 transition-colors hover:bg-emerald-500/25"
        >
          + Neuen User anlegen
        </button>
      </header>

      <div className="flex flex-wrap gap-3">
        <input
          type="search"
          placeholder="Email suchen…"
          data-testid="user-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="min-w-[260px] flex-1 rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-emerald-500/60"
        />
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              data-testid={`filter-${f.key}`}
              onClick={() => {
                setFilter(f.key);
                setOffset(0);
              }}
              className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                filter === f.key
                  ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-200"
                  : "border-zinc-800 text-zinc-400 hover:text-zinc-200"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div
          data-testid="users-error"
          className="rounded-md border border-rose-900 bg-rose-950/40 px-3 py-2 text-sm text-rose-300"
        >
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-zinc-800">
        <table className="w-full text-left text-sm" data-testid="users-table">
          <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Verifiziert</th>
              <th className="px-4 py-3">Pro bis</th>
              <th className="px-4 py-3">Erstellt</th>
              <th className="px-4 py-3 text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/60">
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-zinc-500" data-testid="users-loading">
                  Lade…
                </td>
              </tr>
            )}
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-zinc-500" data-testid="users-empty">
                  Keine User gefunden.
                </td>
              </tr>
            )}
            {!loading &&
              users.map((u) => (
                <tr key={u.ulid} className="hover:bg-zinc-900/40" data-testid={`user-row-${u.ulid}`}>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-200">
                    <div>{u.email}</div>
                    <div className="text-[10px] text-zinc-600">{u.ulid}</div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge user={u} />
                  </td>
                  <td className="px-4 py-3">
                    {u.email_verified ? (
                      <span
                        data-testid={`user-verified-${u.ulid}`}
                        className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-300"
                        title={u.email_verified_at ? fmtDate(u.email_verified_at) : ""}
                      >
                        ✓ Verifiziert
                      </span>
                    ) : (
                      <span
                        data-testid={`user-unverified-${u.ulid}`}
                        className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-300"
                      >
                        Ausstehend
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{fmtDate(u.pro_access_until)}</td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap justify-end gap-1.5">
                      {!u.is_pro && (
                        <button
                          type="button"
                          data-testid={`grant-pro-${u.ulid}`}
                          onClick={() => setGrantTarget(u)}
                          className="rounded-md border border-emerald-500/40 px-2.5 py-1 text-[11px] text-emerald-200 transition-colors hover:bg-emerald-500/10"
                        >
                          Pro 10 J
                        </button>
                      )}
                      {u.is_pro && u.billing_status === "manual" && (
                        <button
                          type="button"
                          data-testid={`revoke-pro-${u.ulid}`}
                          onClick={() => setRevokeTarget(u)}
                          className="rounded-md border border-zinc-700 px-2.5 py-1 text-[11px] text-zinc-300 transition-colors hover:bg-zinc-800"
                        >
                          Pro entfernen
                        </button>
                      )}
                      <button
                        type="button"
                        data-testid={`reset-pw-${u.ulid}`}
                        onClick={() => setResetTarget(u)}
                        className="rounded-md border border-amber-500/40 px-2.5 py-1 text-[11px] text-amber-200 transition-colors hover:bg-amber-500/10"
                      >
                        PW reset
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-zinc-500">
        <span data-testid="users-count">
          {total} User · zeige {users.length} ab Position {offset + 1}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid="page-prev"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - limit))}
            className="rounded-md border border-zinc-800 px-3 py-1.5 text-xs text-zinc-300 transition-colors hover:bg-zinc-900 disabled:opacity-30"
          >
            ← zurück
          </button>
          <button
            type="button"
            data-testid="page-next"
            disabled={offset + limit >= total}
            onClick={() => setOffset(offset + limit)}
            className="rounded-md border border-zinc-800 px-3 py-1.5 text-xs text-zinc-300 transition-colors hover:bg-zinc-900 disabled:opacity-30"
          >
            weiter →
          </button>
        </div>
      </div>

      {createOpen && (
        <CreateUserModal
          onClose={() => setCreateOpen(false)}
          onCreated={onCreatedFromModal}
        />
      )}
      <CredentialsReveal
        open={!!revealData}
        onClose={() => setRevealData(null)}
        email={revealData?.email}
        password={revealData?.password}
      />
      <ConfirmModal
        open={!!grantTarget}
        onClose={() => setGrantTarget(null)}
        onConfirm={onGrantPro}
        title="Pro für 10 Jahre aktivieren"
        body={
          <>
            <strong className="text-zinc-100">{grantTarget?.email}</strong> erhält Pro-Zugang
            für 10 Jahre (manual billing, kein Stripe). Bestehender Trial wird gelöscht.
          </>
        }
        confirmLabel="Pro aktivieren"
        testId="confirm-grant-pro"
      />
      <ConfirmModal
        open={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={onRevokePro}
        title="Pro entfernen"
        body={
          <>
            <strong className="text-zinc-100">{revokeTarget?.email}</strong> verliert sofort
            den Pro-Zugang. Trial-Status (falls vorhanden) bleibt unberührt.
          </>
        }
        confirmLabel="Pro entfernen"
        variant="danger"
        testId="confirm-revoke-pro"
      />
      <ConfirmModal
        open={!!resetTarget}
        onClose={() => setResetTarget(null)}
        onConfirm={onResetPw}
        title="Passwort zurücksetzen"
        body={
          <>
            Für <strong className="text-zinc-100">{resetTarget?.email}</strong> wird ein neues
            20-Zeichen-Passwort generiert und gesetzt. Alle aktiven Sessions werden beendet.
            Das Passwort wird genau einmal angezeigt.
          </>
        }
        confirmLabel="Passwort generieren"
        variant="danger"
        testId="confirm-reset-pw"
      />
    </div>
  );
}
